import { CommonModule, KeyValue } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  EventEmitter,
  inject,
  input,
  model,
  OnInit,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { FormsModule, NgForm, ReactiveFormsModule } from '@angular/forms';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { ClarityModule, ClrForm } from '@clr/angular';
import { ToastService } from 'src/app/services/toast.service';
import { firstValueFrom, Subject } from 'rxjs';
import { InTableValidatorDirective } from 'src/app/directives/validators/in-table-validator.directive';
import { BackendLocaleDatePipe } from 'src/app/pipes/get-locale-date.pipe';
import { MrTranslatePipe } from 'src/app/pipes/mr-translate.pipe';
import { ObjTypePipe as objtype } from 'src/app/pipes/objtype.pipe';
import { APIService } from 'src/app/services/APIService/api.service';
import { LinieDrehungService } from 'src/app/services/Shared/linie-drehung.service';
import { Base64Service, FileObject } from 'src/app/services/base64/base64.service';
import { PreviewThumbnailsComponent } from '../../_shared/preview-thumbnails/preview-thumbnails.component';
import { ArrayMethodsCallbackPipe } from 'src/app/pipes/array-methods-callback.pipe';
import { FileForMediaView, MediaViewModalComponent } from '../../_shared/mediaviewmodal/mediaviewmodal.component';
import { ZaehlerStore } from '../../_tabs/zaehlertab/zaehler.store.service';
import { StammRow } from 'src/app/services/_offline/anlagenDB';
import { EntsperrAnlageService } from 'src/app/services/Shared/entsperr-anlage.service';
import { ConfirmService } from 'src/app/services/confirm.service';
import { DatalistInputComponent } from '../../_shared/datalist-input/datalist-input.component';
import { PrüfungsTypStore } from 'src/app/services/Shared/prüfung-typ.store';
import { getStammValue } from 'src/app/misc/functions';

@Component({
  selector: 'app-stammdatendialog',
  templateUrl: './stammdatendialog.component.html',
  styleUrls: ['./stammdatendialog.component.scss'],
  imports: [
    CommonModule,
    ClarityModule,
    FormsModule,
    ReactiveFormsModule,
    DatalistInputComponent,
    PreviewThumbnailsComponent,
    MediaViewModalComponent,
    InTableValidatorDirective,
    MrTranslatePipe,
    BackendLocaleDatePipe,
    objtype,
    ArrayMethodsCallbackPipe,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StammdatenDialogComponent implements OnInit {
  private readonly zaehlerStore = inject(ZaehlerStore, { optional: true });
  private readonly apiService = inject(APIService);
  private readonly mrTranslate = inject(MrTranslatePipe);
  private readonly domSanitizer = inject(DomSanitizer);
  private readonly toastr = inject(ToastService);
  private readonly base64service = inject(Base64Service);
  private readonly confirmService = inject(ConfirmService);
  protected readonly localeDate = inject(BackendLocaleDatePipe);
  protected readonly drehung = inject(LinieDrehungService);
  private readonly pruefungsTyp = inject(PrüfungsTypStore);

  public readonly closedialog = new Subject<boolean>();
  private dataRefresh: EventEmitter<any>;

  private readonly clrForm = viewChild('stammDatenForm', { read: ClrForm });
  private readonly ngForm = viewChild('stammDatenForm', { read: NgForm });
  private readonly preview = viewChild(PreviewThumbnailsComponent);

  readonly typ_Line_Point = model<number>(undefined);
  readonly otypid = model<number>(undefined);
  readonly oldOPRUEFID = model<number>(undefined);
  readonly opruefid = model<number>(undefined);
  readonly ostammid = model<number>(undefined);
  readonly skipPassbild = input<boolean>(false);

  public set data(inData: any) {
    this.ostammid.set(inData.id);
    this.typ_Line_Point.set(inData.type);
    this.otypid.set(inData.otypid);
    this.oldOPRUEFID.set(inData.opruefid);
    this.opruefid.set(inData.opruefid);
    this.dataRefresh = inData.dataRefresh;
    this.isInspektion.set(false);

    if (this.ostammid() == -1)
      this.titel.set(this.mrTranslate.transform('neue Stammdaten erstellen') + ' - ' + inData.bezeichnung);
  }

  protected readonly stammdaten = signal<StammRow[]>(undefined);

  public get linieValues() {
    const startmeter = +getStammValue(this.stammdaten(), 'StartMeter');
    const endmeter = +getStammValue(this.stammdaten(), 'EndMeter');
    return { startmeter, endmeter };
  }

  protected readonly isInspektion = signal(true);

  protected readonly bemerkung = signal<string>(undefined);
  protected readonly bemerkung_pruefung = this.zaehlerStore?.bemerkung ?? signal<string>(null);

  protected readonly titel = signal('Stammdaten bearbeiten');

  protected readonly skizzenListe = signal<string[]>([]);
  protected readonly selectedSkizzeName = signal<string>('');
  private readonly loadSkizzeVonServer = signal(true);
  private readonly skizze64 = signal<string>(undefined);
  protected readonly skizzeSanitized = computed<SafeUrl>(
    () => this.skizze64() && this.domSanitizer.bypassSecurityTrustUrl(`data:image/png;base64,${this.skizze64()}`)
  );

  protected imageMedia = signal<FileForMediaView>(undefined);
  protected readonly isRep = this.apiService.isRep;

  protected Opt_I_PassBild: boolean = false;
  private deletedFiles: string[] = [];
  protected readonly mapKeysToNumber = (pair: KeyValue<string, unknown>) => +pair.key;

  constructor() {
    inject(EntsperrAnlageService).registerDestroyRef(inject(DestroyRef));

    effect(() => {
      const selected = this.selectedSkizzeName();
      untracked(() => {
        if (!this.loadSkizzeVonServer()) {
          this.loadSkizzeVonServer.set(true);
          return;
        }
        this.skizze64.set(undefined);
        if (selected) this.getSkizzeBase64(selected);
      });
    });
  }

  ngOnInit(): void {
    /* if(this.apiService.isRep) {
      db.getStammInsp(this.ostammid(), this.otypid(), this.typ_Line_Point()).then(
        async ({success, stamm, bemerkung}) => {
          if (success) {
            this.stammdaten.set(stamm);
            this.bemerkung.set(bemerkung);
          } else this.toastr.error(
            this.mrTranslate.transform('Keine Stammdaten gefunden'),
            this.mrTranslate.transform('Etwas ist schief gelaufen')
          );
        }
      );
    } else */
    this.getStammdaten();
  }

  private async getStammdaten() {
    const {
      success,
      error,
      list: stamm,
      bemerkung,
      bemerkung_pruefung,
      skizzenNames,
      selectedSkizze,
      skizzeBase64,
      passbilder,
      istVomHeute,
      opt_I_PassBild,
      opt_F_Zaehler,
    } = await firstValueFrom(
      this.apiService.getStammDaten(
        this.ostammid(),
        this.otypid(),
        this.typ_Line_Point(),
        this.oldOPRUEFID() ?? -1,
        this.isInspektion(),
        this.pruefungsTyp.KPRUVID()
      )
    );

    if (success) {
      this.stammdaten.set(stamm);
      this.bemerkung.set(bemerkung);
      this.bemerkung_pruefung.set(bemerkung_pruefung);
      this.skizzenListe.set(skizzenNames ?? []);
      this.Opt_I_PassBild = opt_I_PassBild;

      if (this.zaehlerStore && !opt_F_Zaehler) {
        this.zaehlerStore.assignedZaehlerName.set(undefined);
      }

      if (skizzeBase64) {
        this.skizze64.set(skizzeBase64);
        this.loadSkizzeVonServer.set(false);
        this.selectedSkizzeName.set(selectedSkizze);
      }
      if (passbilder)
        setTimeout(() => {
          this.preview()?.skipSavedOnPack.set(!this.isInspektion() || istVomHeute);
          this.preview()?.unpackFiles(passbilder);
        });
    } else this.toastr.error(error, this.mrTranslate.transform('Etwas ist schief gelaufen'));
  }

  protected openSkizze() {
    this.imageMedia.set({
      title: 'Messpunktskizze',
      files: [{ base64: this.skizze64() }],
      isSkizze: true,
    });
  }

  //* Ändert gespeicherte Skizze sofort nach Hochladen;
  //? Aber selectedSkizze speichert nur nach "Speichern" button Click
  protected async onBildChanged(file: File) {
    if (!file) return;

    const extension = file.name.split('.').pop();
    if (extension.toLocaleLowerCase() != 'png') {
      this.toastr.warning(this.mrTranslate.transform("Die Skizze soll im Format '.png' sein."));
      return;
    }
    const bildname = file.name.slice(0, -4); /* ".png".length => 4 */

    const isExisting = this.skizzenListe().includes(bildname);

    const ok = () =>
      this.confirmService.warning(
        this.mrTranslate.transform('Es ist bereits eine Skizze mit diesem Namen vorhanden') +
          this.mrTranslate.transform('Möchten Sie die Skizze ersetzen?')
      );

    if (isExisting && !(await ok())) return;

    try {
      const base64 = (await this.base64service.getUrlStringFromFile(file)).split(',').pop();
      const { success, error } = await firstValueFrom(
        this.apiService.setStammSkizze(this.ostammid(), bildname, base64)
      );

      if (success) {
        this.skizze64.set(base64);
        this.toastr.success(this.mrTranslate.transform('Erfolgreich gespeichert!'));
        if (!isExisting) this.skizzenListe.update(names => (names.push(bildname), [...names]));
        this.loadSkizzeVonServer.set(false);
        this.selectedSkizzeName.set(bildname);
        this.dataRefresh?.emit(true);
      } else
        this.toastr.error(
          this.mrTranslate.transform(error),
          this.mrTranslate.transform('Hochladen der Skizze fehlgeschlagen')
        );
    } catch (er) {
      this.toastr.error(
        this.mrTranslate.transform((<ErrorEvent>er).message),
        this.mrTranslate.transform('Hochladen der Skizze fehlgeschlagen')
      );
    }
  }

  public saveIfValid() {
    return this.isFormValid && this.isPassbilderValid && this.saveStammDetails();
  }

  private get isFormValid(): boolean {
    this.clrForm().markAsTouched();
    return this.ngForm().valid;
  }

  private get isPassbilderValid(): boolean {
    const valid =
      this.preview().rawPreviewThumbnails.length > 0 ||
      !this.Opt_I_PassBild ||
      !this.isInspektion() ||
      this.skipPassbild();
    if (!valid)
      this.toastr.error(
        this.mrTranslate.transform('Es muss mindestens ein Passbild im Stammdokument vorhanden sein'),
        this.mrTranslate.transform('Stammdaten')
      );
    return valid;
  }

  protected async saveStammDetails(): Promise<boolean> {
    const sendObj = {
      data: this.stammdaten(),
      selectedSkizze: this.selectedSkizzeName(),
      bemerkung: this.bemerkung(),
      bemerkung_pruefung: this.bemerkung_pruefung(),
      opruefid: this.opruefid(),
      mediaChange: {
        files: await this.preview().packUnsavedFiles(),
        savedFiles: this.preview().savedNames() ?? '',
        deletedFiles: this.deletedFiles,
      },
    };

    const { success, error, ostammid, mediaStr } = await firstValueFrom(
      this.apiService.setStammDaten(this.ostammid(), this.otypid(), this.pruefungsTyp.KPRUVID(), sendObj)
    );

    if (success) {
      this.toastr.success(
        this.mrTranslate.transform('Erfolgreich gespeichert'),
        this.mrTranslate.transform('Stammdaten')
      );
      this.ostammid.set(ostammid);
      this.updatePreview(mediaStr);
      this.dataRefresh?.emit(true);
      return true;
    } else {
      console.log(error);
      this.toastr.error(
        this.mrTranslate.transform('Etwas ist schief gelaufen'),
        this.mrTranslate.transform('Stammdaten')
      );
      return false;
    }
  }

  private async updatePreview(mediaStr: string) {
    this.deletedFiles = [];
    if (!this.preview().unsaved().length) return;
    const files = await firstValueFrom(this.apiService.getPassBilder(mediaStr));
    this.preview().clearPreview();
    this.preview().unpackFiles(files);
  }

  protected calcLaenge() {
    const { startmeter, endmeter } = this.linieValues;
    const laengeRow = this.stammdaten().find(row => row.DBField == 'Laenge');
    const laenge = startmeter === null || endmeter === null ? null : Math.abs((+endmeter * 10 - +startmeter * 10) / 10);

    if (laengeRow) laengeRow.Bezeichnung = laenge;
  }

  protected deleteFile(name: string) {
    this.deletedFiles.push(name);
  }

  private async getSkizzeBase64(value: string) {
    const skizzeBase64 = await this.apiService.getSkizzeByName(value);
    this.skizze64.set(skizzeBase64);
  }

  protected close() {
    this.closedialog.next(true);
  }
}

export interface StammDatenModel {
  data: StammRow[];
  selectedSkizze: string;
  bemerkung: string;
  bemerkung_pruefung?: string;
  opruefid: number;
  mediaChange: {
    files: FileObject[];
    savedFiles: string;
    deletedFiles: string[];
  };
}
